import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserCheck, UserX, TrendingUp, ShoppingCart, RotateCcw, Mail, DollarSign, Search, Power, Eye, Send, History, PenSquare, RefreshCcw, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FLOW_DESCRIPTIONS = {
  welcome: 'Instant welcome email with the 5% first-order discount code',
  abandoned_cart: '3-step recovery sequence at ~1h, ~24h and ~48h',
  post_purchase: 'Thank-you, review request and recommendations after delivery',
  winback: 'Re-engages customers inactive for ~90 days',
};

const TEMPLATE_LABELS = {
  welcome: 'Welcome (5% off)',
  abandoned_1: 'Abandoned Cart · Step 1 (~1h)',
  abandoned_2: 'Abandoned Cart · Step 2 (~24h)',
  abandoned_3: 'Abandoned Cart · Step 3 (~48h)',
  post_purchase_followup: 'Post-Purchase Follow-up',
  post_purchase_review: 'Review Request',
  post_purchase_recommendations: 'Recommendations',
  winback: 'Win-Back',
};

const FLOW_LABELS_LOG = { ...TEMPLATE_LABELS, broadcast: 'Newsletter Broadcast', order_delay: 'Забавяне на поръчка' };

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="em-stat-card" data-testid={`em-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="em-stat-icon"><Icon size={18} /></div>
    <div>
      <div className="em-stat-value">{value}</div>
      <div className="em-stat-label">{label}</div>
    </div>
  </div>
);

const EMPTY_OVERRIDE = { subject: '', title: '', description: '', cta: '' };

const EmailMarketingManager = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState('welcome');
  const [previewLang, setPreviewLang] = useState('bg');

  // Template text editor state
  const [overrides, setOverrides] = useState({ bg: EMPTY_OVERRIDE, en: EMPTY_OVERRIDE });
  const [editLang, setEditLang] = useState('bg');
  const [savingOverride, setSavingOverride] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [overrideMsg, setOverrideMsg] = useState(null);

  // Broadcast state
  const [broadcastForm, setBroadcastForm] = useState({ subject_bg: '', subject_en: '', body_bg: '', body_en: '' });
  const [broadcasts, setBroadcasts] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState(null);

  // Manual order delay notice state
  const [delayForm, setDelayForm] = useState({ email: '', order_number: '', expected_date: '', lang: 'bg' });
  const [sendingDelayNotice, setSendingDelayNotice] = useState(false);
  const [delayMsg, setDelayMsg] = useState(null);

  // Email log state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logSearchInput, setLogSearchInput] = useState('');
  const [logFlowFilter, setLogFlowFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, autoRes] = await Promise.all([
        fetch(`${API_URL}/api/marketing/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/marketing/admin/automations`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (autoRes.ok) setAutomations((await autoRes.json()).automations || []);
    } catch (err) {
      console.error('Failed to load email marketing overview:', err);
    }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/marketing/admin/subscribers?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscribers(data.subscribers || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load subscribers:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Отписване на ${email} от маркетингови имейли?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/subscribers/${encodeURIComponent(email)}/unsubscribe`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) fetchSubscribers();
    } catch (err) {
      console.error('Failed to unsubscribe:', err);
    }
  };

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Изтриване на ${email} завинаги? Това действие не може да бъде отменено.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/subscribers/${encodeURIComponent(email)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) fetchSubscribers();
    } catch (err) {
      console.error('Failed to delete subscriber:', err);
    }
  };

  const fetchOverrides = useCallback(async (template) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/template-overrides/${template}`, { credentials: 'include' });
      if (res.ok) setOverrides(await res.json());
    } catch (err) {
      console.error('Failed to load template overrides:', err);
    }
  }, []);

  const fetchBroadcasts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/broadcasts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const list = data.broadcasts || [];
        setBroadcasts(list);
        return list;
      }
    } catch (err) {
      console.error('Failed to load broadcasts:', err);
    }
    return [];
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(logsPage), limit: '20' });
      if (logSearch) params.set('search', logSearch);
      if (logFlowFilter) params.set('flow', logFlowFilter);
      if (logStatusFilter) params.set('status', logStatusFilter);
      const res = await fetch(`${API_URL}/api/marketing/admin/email-log?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setLogsTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load email log:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPage, logSearch, logFlowFilter, logStatusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === 'subscribers') fetchSubscribers(); }, [activeTab, fetchSubscribers]);
  useEffect(() => { if (activeTab === 'templates') fetchOverrides(previewTemplate); }, [activeTab, previewTemplate, fetchOverrides]);
  useEffect(() => { if (activeTab === 'broadcast') fetchBroadcasts(); }, [activeTab, fetchBroadcasts]);
  useEffect(() => { if (activeTab === 'log') fetchLogs(); }, [activeTab, fetchLogs]);
  useEffect(() => {
    const timer = setTimeout(() => { setLogSearch(logSearchInput); setLogsPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [logSearchInput]);

  const handleToggleAutomation = async (flow) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/automations/${flow}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) fetchAll();
    } catch (err) {
      console.error('Failed to toggle automation:', err);
    }
  };

  const handleSaveOverride = async () => {
    setSavingOverride(true);
    setOverrideMsg(null);
    try {
      const payload = { lang: editLang, ...overrides[editLang] };
      const res = await fetch(`${API_URL}/api/marketing/admin/template-overrides/${previewTemplate}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setPreviewKey((k) => k + 1);
        setOverrideMsg('Запазено');
      } else {
        setOverrideMsg('Грешка при запазване');
      }
    } catch (err) {
      console.error('Failed to save template override:', err);
      setOverrideMsg('Грешка при запазване');
    } finally {
      setSavingOverride(false);
      setTimeout(() => setOverrideMsg(null), 2500);
    }
  };

  const handleResetOverride = async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/template-overrides/${previewTemplate}?lang=${editLang}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setOverrides((prev) => ({ ...prev, [editLang]: EMPTY_OVERRIDE }));
        setPreviewKey((k) => k + 1);
        setOverrideMsg('Възстановен текст по подразбиране');
      }
    } catch (err) {
      console.error('Failed to reset template override:', err);
    } finally {
      setTimeout(() => setOverrideMsg(null), 2500);
    }
  };

  const updateOverrideField = (field, value) => {
    setOverrides((prev) => ({ ...prev, [editLang]: { ...prev[editLang], [field]: value } }));
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setBroadcastMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/broadcasts/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ subject: broadcastForm.subject_bg, body: broadcastForm.body_bg, lang: 'bg' }),
      });
      const data = await res.json();
      if (res.ok) setBroadcastMsg({ type: 'success', text: `Тестов имейл изпратен до ${data.sent_to}` });
      else setBroadcastMsg({ type: 'error', text: data.detail || 'Грешка при изпращане' });
    } catch (err) {
      setBroadcastMsg({ type: 'error', text: 'Грешка при изпращане' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!window.confirm(`Изпратете тази кампания до ${stats?.active_subscribers ?? 0} активни абонати?`)) return;
    setSendingBroadcast(true);
    setBroadcastMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/marketing/admin/broadcasts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(broadcastForm),
      });
      const data = await res.json();
      if (res.ok) {
        setBroadcastMsg({ type: 'success', text: 'Кампанията се изпраща...' });
        setBroadcastForm({ subject_bg: '', subject_en: '', body_bg: '', body_en: '' });
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          const list = await fetchBroadcasts();
          const latest = list && list[0];
          if (latest && latest.id === data.broadcast_id && latest.status === 'sending' && attempts < 15) {
            setTimeout(poll, 2000);
          } else if (latest && latest.id === data.broadcast_id) {
            setBroadcastMsg({ type: 'success', text: `Кампанията е изпратена (${latest.sent_count}/${latest.total_recipients})` });
          }
        };
        setTimeout(poll, 2000);
      } else {
        setBroadcastMsg({ type: 'error', text: data.detail || 'Грешка при изпращане' });
      }
    } catch (err) {
      setBroadcastMsg({ type: 'error', text: 'Грешка при изпращане' });
    } finally {
      setSendingBroadcast(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const handleSendDelayNotice = async () => {
    setSendingDelayNotice(true);
    setDelayMsg(null);
    try {
      let formattedDate = delayForm.expected_date;
      try {
        formattedDate = new Date(delayForm.expected_date).toLocaleDateString(
          delayForm.lang === 'bg' ? 'bg-BG' : 'en-GB',
          { day: 'numeric', month: 'long', year: 'numeric' }
        );
      } catch {
        // fall back to the raw input value
      }
      const res = await fetch(`${API_URL}/api/marketing/admin/send-delay-notice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...delayForm, expected_date: formattedDate }),
      });
      const data = await res.json();
      if (res.ok) {
        setDelayMsg({ type: 'success', text: `Известието е изпратено до ${data.sent_to}` });
        setDelayForm({ email: '', order_number: '', expected_date: '', lang: 'bg' });
      } else {
        setDelayMsg({ type: 'error', text: data.detail || 'Грешка при изпращане' });
      }
    } catch (err) {
      setDelayMsg({ type: 'error', text: 'Грешка при изпращане' });
    } finally {
      setSendingDelayNotice(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / 20));
  const logPages = Math.max(1, Math.ceil(logsTotal / 20));

  const TABS = [
    { key: 'overview', label: 'Обзор' },
    { key: 'templates', label: 'Шаблони' },
    { key: 'broadcast', label: 'Бюлетин' },
    { key: 'delay_notice', label: 'Забавена поръчка' },
    { key: 'log', label: 'Дневник' },
    { key: 'subscribers', label: 'Абонати' },
  ];

  return (
    <div className="email-marketing-manager" data-testid="email-marketing-manager">
      <div className="em-header">
        <h2>Email Marketing</h2>
      </div>

      <div className="em-tabs" data-testid="em-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`em-tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`em-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {stats && (
            <div className="em-stats-grid" data-testid="em-stats-grid">
              <StatCard icon={Users} label="Total Subscribers" value={stats.total_subscribers} />
              <StatCard icon={UserCheck} label="Active Subscribers" value={stats.active_subscribers} />
              <StatCard icon={UserX} label="Unsubscribed" value={stats.unsubscribed} />
              <StatCard icon={TrendingUp} label="New (30d)" value={stats.new_subscribers_30d} />
              <StatCard icon={Mail} label="Popup Subscriptions" value={stats.popup_subscriptions} />
              <StatCard icon={Mail} label="Checkout Subscriptions" value={stats.checkout_subscriptions} />
              <StatCard icon={ShoppingCart} label="Abandoned Carts" value={stats.abandoned_carts} />
              <StatCard icon={RotateCcw} label="Recovered Carts" value={stats.recovered_carts} />
              <StatCard icon={Mail} label="Marketing Emails Sent" value={stats.marketing_emails_sent} />
              <StatCard icon={ShoppingCart} label="Orders From Email" value={stats.orders_from_email} />
              <StatCard icon={DollarSign} label="Revenue From Email" value={`\u20ac${stats.revenue_from_email.toFixed(2)}`} />
            </div>
          )}

          <div className="em-section">
            <h3 className="em-section-title">Automations</h3>
            <div className="em-automations-grid" data-testid="em-automations-grid">
              {automations.map((a) => (
                <div key={a.flow} className={`em-automation-card ${a.enabled ? '' : 'is-disabled'}`} data-testid={`em-automation-${a.flow}`}>
                  <div className="em-automation-header">
                    <h4>{a.label}</h4>
                    <button
                      className={`em-toggle-btn ${a.enabled ? 'is-on' : ''}`}
                      onClick={() => handleToggleAutomation(a.flow)}
                      data-testid={`em-automation-toggle-${a.flow}`}
                      title={a.enabled ? 'Disable' : 'Enable'}
                    >
                      <Power size={14} />
                      <span>{a.enabled ? 'Enabled' : 'Disabled'}</span>
                    </button>
                  </div>
                  <p className="em-automation-desc">{FLOW_DESCRIPTIONS[a.flow]}</p>
                  <div className="em-automation-metrics">
                    <div><span className="em-metric-value">{a.users_in_flow}</span><span className="em-metric-label">In Flow</span></div>
                    <div><span className="em-metric-value">{a.emails_sent}</span><span className="em-metric-label">Sent</span></div>
                    <div><span className="em-metric-value">{a.conversions}</span><span className="em-metric-label">Conversions</span></div>
                    <div><span className="em-metric-value">&euro;{a.revenue.toFixed(2)}</span><span className="em-metric-label">Revenue</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'templates' && (
        <div className="em-section">
          <div className="em-preview-header">
            <h3 className="em-section-title">Email Templates</h3>
            <div className="em-preview-lang-toggle" data-testid="em-preview-lang-toggle">
              <button className={editLang === 'bg' ? 'is-active' : ''} onClick={() => { setEditLang('bg'); setPreviewLang('bg'); }} data-testid="em-preview-lang-bg">BG</button>
              <button className={editLang === 'en' ? 'is-active' : ''} onClick={() => { setEditLang('en'); setPreviewLang('en'); }} data-testid="em-preview-lang-en">EN</button>
            </div>
          </div>
          <div className="em-preview-layout">
            <div className="em-preview-list" data-testid="em-preview-template-list">
              {Object.entries(TEMPLATE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  className={`em-preview-list-item ${previewTemplate === key ? 'is-active' : ''}`}
                  onClick={() => setPreviewTemplate(key)}
                  data-testid={`em-preview-select-${key}`}
                >
                  <Eye size={13} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
            <div className="em-preview-content">
              <div className="em-edit-panel" data-testid="em-template-edit-panel">
                <p className="em-edit-hint">
                  <PenSquare size={13} /> Редактирайте текста на автоматизирания имейл ({editLang.toUpperCase()}). Оставете поле празно, за да ползвате текста по подразбиране.
                </p>
                <div className="em-edit-grid">
                  <div className="em-edit-field">
                    <label>Тема на имейла</label>
                    <input type="text" value={overrides[editLang]?.subject || ''} onChange={(e) => updateOverrideField('subject', e.target.value)} data-testid="em-override-subject" />
                  </div>
                  <div className="em-edit-field">
                    <label>Заглавие (H2)</label>
                    <input type="text" value={overrides[editLang]?.title || ''} onChange={(e) => updateOverrideField('title', e.target.value)} data-testid="em-override-title" />
                  </div>
                  <div className="em-edit-field em-edit-field-full">
                    <label>Описание / основен текст</label>
                    <textarea rows={3} value={overrides[editLang]?.description || ''} onChange={(e) => updateOverrideField('description', e.target.value)} data-testid="em-override-description" />
                  </div>
                  <div className="em-edit-field">
                    <label>Текст на бутона</label>
                    <input type="text" value={overrides[editLang]?.cta || ''} onChange={(e) => updateOverrideField('cta', e.target.value)} data-testid="em-override-cta" />
                  </div>
                </div>
                <div className="em-edit-actions">
                  <button className="em-btn-primary" onClick={handleSaveOverride} disabled={savingOverride} data-testid="em-save-override-btn">
                    <Save size={14} /> {savingOverride ? 'Запазване...' : 'Запази'}
                  </button>
                  <button className="em-btn-ghost" onClick={handleResetOverride} data-testid="em-reset-override-btn">
                    <RefreshCcw size={14} /> Върни по подразбиране
                  </button>
                  {overrideMsg && <span className="em-inline-msg" data-testid="em-override-message">{overrideMsg}</span>}
                </div>
              </div>
              <div className="em-preview-frame-wrap">
                <iframe
                  key={`${previewTemplate}-${previewLang}-${previewKey}`}
                  className="em-preview-frame"
                  title="Email template preview"
                  src={`${API_URL}/api/marketing/admin/preview/${previewTemplate}?lang=${previewLang}`}
                  data-testid="em-preview-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'broadcast' && (
        <div className="em-section">
          <h3 className="em-section-title"><Send size={16} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />Изпрати бюлетин до абонатите</h3>
          <p className="em-broadcast-hint">Ще бъде изпратен до <strong>{stats?.active_subscribers ?? 0}</strong> активни абонати (BG абонати получават BG версията, EN — EN версията). Абзаците се разделят с празен ред.</p>
          <div className="em-broadcast-form" data-testid="em-broadcast-form">
            <div className="em-broadcast-cols">
              <div className="em-edit-field">
                <label>Тема (BG) *</label>
                <input type="text" value={broadcastForm.subject_bg} onChange={(e) => setBroadcastForm((p) => ({ ...p, subject_bg: e.target.value }))} data-testid="em-broadcast-subject-bg" />
              </div>
              <div className="em-edit-field">
                <label>Тема (EN)</label>
                <input type="text" value={broadcastForm.subject_en} onChange={(e) => setBroadcastForm((p) => ({ ...p, subject_en: e.target.value }))} data-testid="em-broadcast-subject-en" />
              </div>
            </div>
            <div className="em-broadcast-cols">
              <div className="em-edit-field">
                <label>Съдържание (BG) *</label>
                <textarea rows={6} value={broadcastForm.body_bg} onChange={(e) => setBroadcastForm((p) => ({ ...p, body_bg: e.target.value }))} data-testid="em-broadcast-body-bg" />
              </div>
              <div className="em-edit-field">
                <label>Съдържание (EN)</label>
                <textarea rows={6} value={broadcastForm.body_en} onChange={(e) => setBroadcastForm((p) => ({ ...p, body_en: e.target.value }))} data-testid="em-broadcast-body-en" />
              </div>
            </div>
            {broadcastMsg && (
              <div className={`em-broadcast-msg ${broadcastMsg.type}`} data-testid="em-broadcast-message">{broadcastMsg.text}</div>
            )}
            <div className="em-edit-actions">
              <button
                className="em-btn-ghost"
                onClick={handleSendTest}
                disabled={sendingTest || !broadcastForm.subject_bg || !broadcastForm.body_bg}
                data-testid="em-send-test-btn"
              >
                <Mail size={14} /> {sendingTest ? 'Изпращане...' : 'Изпрати тест до мен'}
              </button>
              <button
                className="em-btn-primary"
                onClick={handleSendBroadcast}
                disabled={sendingBroadcast || !broadcastForm.subject_bg || !broadcastForm.body_bg}
                data-testid="em-send-broadcast-btn"
              >
                <Send size={14} /> {sendingBroadcast ? 'Изпращане...' : 'Изпрати до всички'}
              </button>
            </div>
          </div>

          <h3 className="em-section-title" style={{ marginTop: 32 }}><History size={16} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />История на кампаниите</h3>
          <div className="em-table-wrap">
            <table className="em-table" data-testid="em-broadcasts-table">
              <thead>
                <tr><th>Тема</th><th>Статус</th><th>Получатели</th><th>Изпратени</th><th>Неуспешни</th><th>Дата</th></tr>
              </thead>
              <tbody>
                {broadcasts.length === 0 ? (
                  <tr><td colSpan={6} className="em-table-empty">Все още няма изпратени кампании.</td></tr>
                ) : broadcasts.map((b) => (
                  <tr key={b.id} data-testid={`em-broadcast-row-${b.id}`}>
                    <td>{b.subject_bg}</td>
                    <td><span className={`em-status-badge ${b.status === 'sent' ? 'subscribed' : 'unsubscribed'}`}>{b.status}</span></td>
                    <td>{b.total_recipients}</td>
                    <td>{b.sent_count}</td>
                    <td>{b.failed_count}</td>
                    <td>{formatDate(b.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'delay_notice' && (
        <div className="em-section">
          <h3 className="em-section-title"><Mail size={16} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />Известие за забавена поръчка</h3>
          <p className="em-broadcast-hint">
            Ръчно изпращане на извинение до клиент, чиято поръчка се забавя. Въведете имейла на получателя и датата,
            до която да очаква поръчката си - съдържанието на писмото е готов шаблон.
          </p>
          <div className="em-broadcast-form" data-testid="em-delay-notice-form">
            <div className="em-broadcast-cols">
              <div className="em-edit-field">
                <label>Имейл на получателя *</label>
                <input
                  type="email"
                  placeholder="klient@example.com"
                  value={delayForm.email}
                  onChange={(e) => setDelayForm((p) => ({ ...p, email: e.target.value }))}
                  data-testid="em-delay-email-input"
                />
              </div>
              <div className="em-edit-field">
                <label>Номер на поръчка (незадължително)</label>
                <input
                  type="text"
                  placeholder="KOS-260824-XXXX"
                  value={delayForm.order_number}
                  onChange={(e) => setDelayForm((p) => ({ ...p, order_number: e.target.value }))}
                  data-testid="em-delay-order-number-input"
                />
              </div>
            </div>
            <div className="em-broadcast-cols">
              <div className="em-edit-field">
                <label>Очаквайте поръчката до *</label>
                <input
                  type="date"
                  value={delayForm.expected_date}
                  onChange={(e) => setDelayForm((p) => ({ ...p, expected_date: e.target.value }))}
                  data-testid="em-delay-expected-date-input"
                />
              </div>
              <div className="em-edit-field">
                <label>Език на писмото</label>
                <select
                  value={delayForm.lang}
                  onChange={(e) => setDelayForm((p) => ({ ...p, lang: e.target.value }))}
                  data-testid="em-delay-lang-select"
                >
                  <option value="bg">Български</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
            {delayMsg && (
              <div className={`em-broadcast-msg ${delayMsg.type}`} data-testid="em-delay-notice-message">{delayMsg.text}</div>
            )}
            <div className="em-edit-actions">
              <button
                className="em-btn-primary"
                onClick={handleSendDelayNotice}
                disabled={sendingDelayNotice || !delayForm.email || !delayForm.expected_date}
                data-testid="em-send-delay-notice-btn"
              >
                <Send size={14} /> {sendingDelayNotice ? 'Изпращане...' : 'Изпрати известие'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'log' && (
        <div className="em-section">
          <h3 className="em-section-title">Дневник на изпратени имейли</h3>
          <div className="em-subscribers-filters">
            <div className="em-search-box">
              <Search size={15} />
              <input
                type="text"
                placeholder="Търси по имейл..."
                value={logSearchInput}
                onChange={(e) => setLogSearchInput(e.target.value)}
                data-testid="em-log-search"
              />
            </div>
            <select value={logFlowFilter} onChange={(e) => { setLogFlowFilter(e.target.value); setLogsPage(1); }} data-testid="em-log-flow-filter">
              <option value="">Всички имейли</option>
              {Object.entries(FLOW_LABELS_LOG).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select value={logStatusFilter} onChange={(e) => { setLogStatusFilter(e.target.value); setLogsPage(1); }} data-testid="em-log-status-filter">
              <option value="">Всички статуси</option>
              <option value="sent">Изпратен</option>
              <option value="failed">Неуспешен</option>
            </select>
          </div>

          <div className="em-table-wrap">
            <table className="em-table" data-testid="em-log-table">
              <thead>
                <tr><th>Имейл</th><th>Тип</th><th>Статус</th><th>Дата</th></tr>
              </thead>
              <tbody>
                {logsLoading ? (
                  <tr><td colSpan={4} className="em-table-empty">Зареждане...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={4} className="em-table-empty">Няма записи.</td></tr>
                ) : logs.map((l, idx) => (
                  <tr key={idx} data-testid={`em-log-row-${idx}`}>
                    <td>{l.email}</td>
                    <td>{FLOW_LABELS_LOG[l.flow] || l.flow}</td>
                    <td><span className={`em-status-badge ${l.status === 'sent' ? 'subscribed' : 'unsubscribed'}`}>{l.status === 'sent' ? 'Изпратен' : 'Неуспешен'}</span></td>
                    <td>{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {logPages > 1 && (
            <div className="em-pagination" data-testid="em-log-pagination">
              <button disabled={logsPage <= 1} onClick={() => setLogsPage((p) => p - 1)}>Prev</button>
              <span>Page {logsPage} of {logPages}</span>
              <button disabled={logsPage >= logPages} onClick={() => setLogsPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'subscribers' && (
        <div className="em-section">
          <h3 className="em-section-title">Subscribers</h3>
          <div className="em-subscribers-filters">
            <div className="em-search-box">
              <Search size={15} />
              <input
                type="text"
                placeholder="Search by email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                data-testid="em-subscribers-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              data-testid="em-subscribers-status-filter"
            >
              <option value="">All statuses</option>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>

          <div className="em-table-wrap">
            <table className="em-table" data-testid="em-subscribers-table">
              <thead>
                <tr>
                  <th>Email</th><th>Customer</th><th>Status</th><th>Source</th><th>Subscribed</th><th>Unsubscribed</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="em-table-empty">Loading...</td></tr>
                ) : subscribers.length === 0 ? (
                  <tr><td colSpan={7} className="em-table-empty">No subscribers found.</td></tr>
                ) : subscribers.map((s) => (
                  <tr key={s.email} data-testid={`em-subscriber-row-${s.email}`}>
                    <td>{s.email}</td>
                    <td>{s.customer ? 'Yes' : 'No'}</td>
                    <td><span className={`em-status-badge ${s.status}`}>{s.status}</span></td>
                    <td>{s.source}</td>
                    <td>{formatDate(s.subscribed_at)}</td>
                    <td>{formatDate(s.unsubscribed_at)}</td>
                    <td className="em-subscriber-actions">
                      {s.status === 'subscribed' && (
                        <button
                          className="em-action-btn"
                          onClick={() => handleUnsubscribe(s.email)}
                          data-testid={`em-unsubscribe-btn-${s.email}`}
                        >
                          Unsubscribe
                        </button>
                      )}
                      <button
                        className="em-action-btn em-action-btn-danger"
                        onClick={() => handleDeleteSubscriber(s.email)}
                        data-testid={`em-delete-subscriber-btn-${s.email}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="em-pagination" data-testid="em-pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
              <span>Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      <style>{`
        .em-subscriber-actions { display: flex; gap: 6px; white-space: nowrap; }
        .em-action-btn { font-size: 11px; padding: 5px 10px; border-radius: 5px; border: 1px solid var(--border-light); background: var(--bg-secondary); color: var(--text-primary); cursor: pointer; transition: all 0.15s; }
        .em-action-btn:hover { border-color: var(--accent-gold, #c9a86c); color: var(--accent-gold, #c9a86c); }
        .em-action-btn-danger:hover { border-color: #d9534f; color: #d9534f; }
        .email-marketing-manager { padding: 8px 0; }
        .em-header h2 { margin: 0 0 20px; font-size: 22px; font-weight: 600; color: var(--text-primary); }
        .em-tabs { display: flex; gap: 4px; margin-bottom: 24px; border-bottom: 1px solid var(--border-light); overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .em-tab { flex-shrink: 0; padding: 10px 18px; background: none; border: none; border-bottom: 2px solid transparent; font-size: 13px; font-weight: 500; color: var(--text-secondary); cursor: pointer; white-space: nowrap; transition: all 0.15s; }
        .em-tab:hover { color: var(--text-primary); }
        .em-tab.is-active { color: var(--text-primary); border-bottom-color: var(--accent-gold, #c9a86c); font-weight: 600; }
        .em-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
        .em-stat-card { display: flex; align-items: center; gap: 12px; background: var(--bg-secondary); border: 1px solid var(--border-light); border-radius: 8px; padding: 16px; }
        .em-stat-icon { width: 36px; height: 36px; border-radius: 8px; background: var(--bg-primary); display: flex; align-items: center; justify-content: center; color: var(--accent-gold, #c9a86c); flex-shrink: 0; }
        .em-stat-value { font-size: 20px; font-weight: 700; color: var(--text-primary); line-height: 1.1; }
        .em-stat-label { font-size: 11px; color: var(--text-secondary); margin-top: 2px; }
        .em-section { margin-bottom: 36px; }
        .em-section-title { font-size: 16px; font-weight: 600; color: var(--text-primary); margin: 0 0 16px; }
        .em-automations-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .em-automation-card { background: var(--bg-secondary); border: 1px solid var(--border-light); border-radius: 8px; padding: 18px; transition: opacity 0.2s; }
        .em-automation-card.is-disabled { opacity: 0.55; }
        .em-automation-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .em-automation-header h4 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .em-automation-desc { font-size: 12px; color: var(--text-secondary); margin: 0 0 14px; line-height: 1.4; }
        .em-toggle-btn { display: flex; align-items: center; gap: 6px; border: 1px solid var(--border-light); background: var(--bg-primary); color: var(--text-secondary); padding: 6px 12px; border-radius: 20px; font-size: 12px; cursor: pointer; }
        .em-toggle-btn.is-on { background: #1a7a3c; border-color: #1a7a3c; color: #fff; }
        .em-automation-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
        .em-automation-metrics > div { display: flex; flex-direction: column; }
        .em-metric-value { font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .em-metric-label { font-size: 10px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }
        .em-subscribers-filters { display: flex; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
        .em-search-box { display: flex; align-items: center; gap: 8px; border: 1px solid var(--border-light); border-radius: 6px; padding: 8px 12px; background: var(--bg-primary); flex: 1; max-width: 320px; color: var(--text-secondary); }
        .em-search-box input { border: none; outline: none; background: none; flex: 1; font-size: 13px; color: var(--text-primary); }
        .em-subscribers-filters select { border: 1px solid var(--border-light); border-radius: 6px; padding: 8px 12px; font-size: 13px; background: var(--bg-primary); color: var(--text-primary); }
        .em-table-wrap { overflow-x: auto; border: 1px solid var(--border-light); border-radius: 8px; }
        .em-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .em-table th, .em-table td { text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--border-light); white-space: nowrap; }
        .em-table th { background: var(--bg-secondary); color: var(--text-secondary); font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
        .em-table-empty { text-align: center; color: var(--text-secondary); padding: 24px !important; }
        .em-status-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; text-transform: capitalize; }
        .em-status-badge.subscribed { background: rgba(26, 122, 60, 0.12); color: #1a7a3c; }
        .em-status-badge.unsubscribed { background: rgba(220, 38, 38, 0.1); color: #dc2626; }
        .em-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 14px; font-size: 13px; color: var(--text-secondary); }
        .em-pagination button { border: 1px solid var(--border-light); background: var(--bg-primary); border-radius: 6px; padding: 6px 14px; cursor: pointer; color: var(--text-primary); }
        .em-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
        .em-preview-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .em-preview-lang-toggle { display: flex; border: 1px solid var(--border-light); border-radius: 6px; overflow: hidden; }
        .em-preview-lang-toggle button { border: none; background: var(--bg-primary); color: var(--text-secondary); padding: 6px 16px; font-size: 12px; font-weight: 600; cursor: pointer; }
        .em-preview-lang-toggle button.is-active { background: var(--text-primary); color: var(--bg-primary); }
        .em-preview-layout { display: grid; grid-template-columns: 220px 1fr; gap: 16px; }
        .em-preview-list { display: flex; flex-direction: column; gap: 4px; }
        .em-preview-list-item { display: flex; align-items: center; gap: 8px; text-align: left; border: 1px solid transparent; background: var(--bg-secondary); color: var(--text-secondary); padding: 10px 12px; border-radius: 6px; font-size: 12.5px; cursor: pointer; transition: all 0.15s; }
        .em-preview-list-item:hover { background: var(--bg-primary); }
        .em-preview-list-item.is-active { background: var(--bg-primary); border-color: var(--accent-gold, #c9a86c); color: var(--text-primary); font-weight: 600; }
        .em-preview-content { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
        .em-edit-panel { background: var(--bg-secondary); border: 1px solid var(--border-light); border-radius: 8px; padding: 16px; }
        .em-edit-hint { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-secondary); margin: 0 0 14px; }
        .em-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .em-edit-field-full { grid-column: 1 / -1; }
        .em-edit-field { display: flex; flex-direction: column; gap: 6px; }
        .em-edit-field label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-secondary); }
        .em-edit-field input, .em-edit-field textarea { border: 1px solid var(--border-light); border-radius: 6px; padding: 8px 10px; font-size: 13px; background: var(--bg-primary); color: var(--text-primary); font-family: inherit; resize: vertical; }
        .em-edit-field input:focus, .em-edit-field textarea:focus { outline: none; border-color: var(--accent-gold, #c9a86c); }
        .em-edit-actions { display: flex; gap: 10px; margin-top: 14px; }
        .em-btn-primary { display: flex; align-items: center; gap: 6px; border: none; background: var(--text-primary); color: var(--bg-primary); padding: 10px 18px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; transition: opacity 0.15s; }
        .em-btn-primary:hover:not(:disabled) { opacity: 0.85; }
        .em-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .em-btn-ghost { display: flex; align-items: center; gap: 6px; border: 1px solid var(--border-light); background: var(--bg-primary); color: var(--text-primary); padding: 10px 18px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.15s; }
        .em-btn-ghost:hover:not(:disabled) { border-color: var(--accent-gold, #c9a86c); }
        .em-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }
        .em-preview-frame-wrap { border: 1px solid var(--border-light); border-radius: 8px; overflow: hidden; background: #fff; height: 560px; }
        .em-preview-frame { width: 100%; height: 100%; border: none; }
        .em-broadcast-hint { font-size: 13px; color: var(--text-secondary); margin: 0 0 16px; }
        .em-broadcast-form { background: var(--bg-secondary); border: 1px solid var(--border-light); border-radius: 8px; padding: 18px; }
        .em-broadcast-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
        .em-broadcast-msg { padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 12px; }
        .em-broadcast-msg.success { background: rgba(26, 122, 60, 0.12); color: #1a7a3c; }
        .em-broadcast-msg.error { background: rgba(220, 38, 38, 0.1); color: #dc2626; }
        .em-inline-msg { font-size: 12px; color: #1a7a3c; align-self: center; }
        @media (max-width: 1024px) { .em-stats-grid { grid-template-columns: repeat(2, 1fr); } .em-automations-grid { grid-template-columns: 1fr; } .em-preview-layout { grid-template-columns: 1fr; } .em-preview-list { flex-direction: row; overflow-x: auto; } }
        @media (max-width: 640px) { .em-stats-grid { grid-template-columns: 1fr 1fr; } .em-automation-metrics { grid-template-columns: repeat(2, 1fr); } .em-edit-grid { grid-template-columns: 1fr; } .em-broadcast-cols { grid-template-columns: 1fr; } .em-edit-actions { flex-direction: column; } }
      `}</style>
    </div>
  );
};

export default EmailMarketingManager;
